import {
  SHButton,
  SHDotGroupProps,
  SHLabel,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { PlusSVG } from "@components/svgs";
import { useCollapseMenu } from "@hooks/useCollapseMenu ";
import { useUpdateModelsTab } from "@hooks/useInvestmentProduct";
import { HighlightType } from "@models/configuration";
import { InvestmentProductConfigurationSection } from "@models/product/investment-product/entities/investmentProduct";
import { InvestmentProductSeriesDTO } from "@models/product/investment-product/entities/series";
import {
  InvestmentDataStatus,
  InvestmentProductSeriesRoleOfEsgConsiderations,
} from "@models/product/investment-product/enums/status";
import { useTheme } from "@mui/material";
import { SeriesPath } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/series";
import {
  InvestmentProductSeriesStaticId,
  SeriesDependedFeatureStaticId,
} from "@pages/suppliers/constants";
import { toggleSaveDraftButtonDisabled } from "@redux/slices/product/investment-product";
import { useAppDispatch } from "@redux/store";
import { generateUUID, hexToRGBA } from "@utils";
import { isEmpty, isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMount } from "react-use";

export interface ProductCollapseMenuProps {
  investmentProductSeriesData?: InvestmentProductSeriesDTO[];
  selectedIndex?: [number, number];
  defaultExpandIndexes?: number[];
  onChange?: (index: [number, number]) => void;
  dotGroupProps?: Omit<SHDotGroupProps, "disabled">;
  onAddNewModelClick?: () => void;
  isDisabled?: boolean;
  mode?: "view" | "edit";
  allowAddNewSeriesModel?: boolean;
}

const EditSeriesCollapseMenu = ({
  investmentProductSeriesData,
  selectedIndex = [0, 0],
  defaultExpandIndexes = [],
  onChange = (index) => {},
  onAddNewModelClick,
  isDisabled = false,
  mode = "view",
  allowAddNewSeriesModel = false,
}: ProductCollapseMenuProps) => {
  const { palette } = useTheme();

  const { expandedIndex, handleCollapseToggle } =
    useCollapseMenu(defaultExpandIndexes);
  const { getValues } = useFormContext<{
    investmentProductSeriesData: InvestmentProductSeriesDTO[];
  }>();
  const [isMounted, setIsMounted] = useState(false);
  const dispatch = useAppDispatch();
  const handleUpdateModelsTab = useUpdateModelsTab();

  const seriesData = useWatch({
    name: "investmentProductSeriesData",
  });

  const menuDotStatus = useMemo(() => {
    const investmentProductSeriesDataForm =
      (seriesData as InvestmentProductSeriesDTO[]) ?? [];
    const seriesOrangeDotStatusMap = new Map<
      string,
      {
        seriesIndex: number;
        sectionIndex: number;
        isModified: boolean;
        isShowOrangeDot: boolean;
        isShowBlueDot: boolean;
      }
    >();

    for (
      let seriesIdx = 0;
      seriesIdx < investmentProductSeriesDataForm.length;
      seriesIdx++
    ) {
      const sections =
        investmentProductSeriesDataForm[seriesIdx].sections ?? [];
      for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
        const section = sections[sectionIdx];
        const features = section?.features ?? [];
        const key = `${seriesIdx}-${sectionIdx}`;

        if (!seriesOrangeDotStatusMap.has(key)) {
          seriesOrangeDotStatusMap.set(key, {
            seriesIndex: seriesIdx,
            sectionIndex: sectionIdx,
            isModified: false,
            isShowOrangeDot: false,
            isShowBlueDot: false,
          });
        }

        const dotStatus = seriesOrangeDotStatusMap.get(key)!;

        const isResponsibleInvestmentSection = isEqual(
          section.staticId,
          InvestmentProductSeriesStaticId.SectionResponsibleInvestmentPractices,
        );
        
        const roleOfEsgFeature = features.find(
          (x) =>
            x.staticId === InvestmentProductSeriesStaticId.FeatureRoleOfESG,
        );

        for (const feature of features) {
          const productData = feature.productData;
          const isMissingData = isEqual(
            productData?.dataStatus,
            InvestmentDataStatus.MissingData,
          );

          if (productData?.isModified) {
            dotStatus.isModified = true;
          }

          if (isEqual(productData?.highlightType, HighlightType.Edited)) {
            dotStatus.isShowOrangeDot = true;
          }

          if (isMissingData) {
            if (isResponsibleInvestmentSection) {
              const isNoRole = isEqual(
                roleOfEsgFeature?.productData?.value,
                InvestmentProductSeriesRoleOfEsgConsiderations.NoRole,
              );
              const isDependentFeature = SeriesDependedFeatureStaticId.includes(
                feature.staticId ?? "",
              );

              if (!(isNoRole && isDependentFeature)) {
                dotStatus.isShowBlueDot = true;
              }
            } else {
              dotStatus.isShowBlueDot = true;
            }
          }
        }
      }
    }

    return Array.from(seriesOrangeDotStatusMap.values());
  }, [seriesData]);
  const isAnySeriesCodeChanged = useCallback(() => {
    const draftedSeriesCodes = investmentProductSeriesData?.map(
      (series: InvestmentProductSeriesDTO) => series.seriesCode,
    );
    const formDataSeriesCodes = seriesData?.map(
      (series: InvestmentProductSeriesDTO) => series.seriesCode,
    );
    return !isEqual(draftedSeriesCodes, formDataSeriesCodes);
  }, [investmentProductSeriesData, seriesData]);

  useMount(() => {
    setIsMounted(true);
  });

  useEffect(() => {
    const isAnySectionEdited = menuDotStatus.some(
      (status) => status.isShowOrangeDot,
    );
    const isAnySectionMissingData = menuDotStatus.some(
      (status) => status.isShowBlueDot,
    );
    const isAnySectionModified = menuDotStatus.some(
      (status) => status.isModified,
    );
    if (isMounted) {
      handleUpdateModelsTab({
        isAnySectionModified,
        isAnySectionEdited,
        isAnySectionMissingData,
      });

      dispatch(
        toggleSaveDraftButtonDisabled(
          !isAnySectionModified && !isAnySeriesCodeChanged(),
        ),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData]);

  const handleCreateConfigurationSeriesModel = () => {
    onAddNewModelClick && onAddNewModelClick();
  };

  if (!investmentProductSeriesData) {
    return <></>;
  }

  if (investmentProductSeriesData.length === 0) {
    return (
      <SHStack
        direction={"row"}
        spacing={0.5}
        justifyContent="space-between"
        alignItems="center"
        p={1}
      >
        <SHTypography p={0} variant="subtitle1">
          Create series
        </SHTypography>
        <SHButton
          // isLoading={isCreatingModel}
          // disabled={isCreatingModel}
          variant="text"
          size="medium"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleCreateConfigurationSeriesModel();
          }}
          startIcon={<PlusSVG color={palette.primary.main} />}
        >
          New
        </SHButton>
      </SHStack>
    );
  }

  return (
    <>
      {investmentProductSeriesData.map((group, seriesIndex) => {
        const isExpanded = expandedIndex.includes(seriesIndex);
        const seriesFeatureDataPath = `investmentProductSeriesData.${seriesIndex}.sections.0.features.0`;
        const isEmptyModel = isEmpty(group.sections);
        const seriesNameValue = getValues(
          `${seriesFeatureDataPath}.productData.value` as SeriesPath,
        ) as string;
        const seriesNameOrangeDotStatus = menuDotStatus.some(
          (status) =>
            status.isShowOrangeDot && status.seriesIndex === seriesIndex,
        );
        const seriesNameBlueDotStatus = menuDotStatus.some(
          (status) =>
            status.isShowBlueDot && status.seriesIndex === seriesIndex,
        );

        return (
          <SHCollapse
            key={group.id ?? generateUUID()}
            title={
              <SHStack
                direction="row"
                spacing={0.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <SHStack direction={"row"} gap={1}>
                  <SHLabel
                    dotGroupProps={{
                      orangeDot: seriesNameOrangeDotStatus,
                      blueDot: seriesNameBlueDotStatus,
                    }}
                    label={
                      <SHTypography
                        p={0}
                        variant="subtitle1"
                        sx={{
                          maxWidth: "170px",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          wordBreak: "normal",
                        }}
                      >
                        {seriesNameValue}
                      </SHTypography>
                    }
                  />
                </SHStack>
                {mode === "edit" && allowAddNewSeriesModel && (
                  <SHButton
                    variant="text"
                    size="medium"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateConfigurationSeriesModel();
                    }}
                    disabled={isDisabled}
                    startIcon={
                      <PlusSVG
                        color={
                          isDisabled
                            ? palette.text.disabled
                            : palette.primary.main
                        }
                      />
                    }
                  >
                    New
                  </SHButton>
                )}
              </SHStack>
            }
            open={isExpanded}
            onCollapse={(open) => {
              handleCollapseToggle(seriesIndex, open);
            }}
            width={"100%"}
            height="auto"
            sx={{
              padding: "20px 10px",
              cursor: "pointer",
              bgcolor: isExpanded ? palette.common.white : "transparent",
              borderColor: palette.secondary[100],
              borderBottomStyle: "solid",
              borderBottomWidth: isExpanded ? 0 : 1,
            }}
          >
            {isEmptyModel ? (
              <SHTypography
                variant="body2"
                sx={{
                  padding: "20px 10px",
                }}
              >
                No models
              </SHTypography>
            ) : (
              group.sections?.map(
                (
                  section: InvestmentProductConfigurationSection,
                  sectionIndex,
                ) => {
                  const selectedSubSection =
                    selectedIndex[0] === seriesIndex &&
                    selectedIndex[1] === sectionIndex;
                  const sectionDotStatus = menuDotStatus.find(
                    (status) =>
                      status.seriesIndex === seriesIndex &&
                      status.sectionIndex === sectionIndex,
                  );
                  return (
                    <SHStack
                      key={section.id}
                      sx={{
                        padding: "20px 10px",
                        cursor: "pointer",
                        bgcolor: selectedSubSection
                          ? hexToRGBA(palette.primary.light, 0.1)
                          : "transparent",
                        "&:hover": {
                          bgcolor: selectedSubSection
                            ? hexToRGBA(palette.primary.light, 0.15)
                            : hexToRGBA(palette.common.white, 0.5),
                        },
                      }}
                      onClick={() => {
                        onChange([seriesIndex, sectionIndex]);
                      }}
                      direction="row"
                      spacing={0.5}
                      alignItems="start"
                    >
                      <SHLabel
                        dotGroupProps={{
                          orangeDot: sectionDotStatus?.isShowOrangeDot,
                          blueDot: sectionDotStatus?.isShowBlueDot,
                        }}
                        label={
                          <SHTypography
                            variant={selectedSubSection ? "subtitle2" : "body1"}
                          >
                            {section.name}
                          </SHTypography>
                        }
                      />
                    </SHStack>
                  );
                },
              )
            )}
          </SHCollapse>
        );
      })}
    </>
  );
};

export default EditSeriesCollapseMenu;
